import { Injectable, inject } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { filter } from 'rxjs/operators';

@Injectable()
export class CustomAlertService extends AlertService {
  private readonly STRONG_ALERT_TIMEOUT_MS = 30_000;

  private router = inject(Router);
  private currentStrongTimestamp: number | null = null;
  private strongAlertTimer: ReturnType<typeof setTimeout> | null = null;
  private formChangeHandler: ((event: Event) => void) | null = null;

  constructor() {
    super();
    this.router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => {
      this.dismissCurrentStrongAlert();
    });
  }

  override showAlert(alert: Alert): void {
    if (alert.type === AlertType.strong) {
      this.dismissCurrentStrongAlert();
      super.showAlert(alert);
      this.currentStrongTimestamp = alert.timestamp;
      const captured = alert.timestamp;
      this.strongAlertTimer = setTimeout(() => {
        // Edge case handling if a second alert is triggered before the first ends
        // Clears old alerts that wasn't cleared before it's timout was overwritten by a new alert
        if (this.currentStrongTimestamp === captured) {
          this.dismissCurrentStrongAlert();
        }
      }, this.STRONG_ALERT_TIMEOUT_MS); // 30s timeout for strong alerts
      this.setupFormChangeListener();
    } else {
      super.showAlert(alert);
    }
  }

  private setupFormChangeListener(): void {
    setTimeout(() => {
      if (this.currentStrongTimestamp === null) return;
      this.formChangeHandler = (event: Event) => {
        // Only dismiss when the click is inside a CDK overlay panel
        // (mic-dropdown-search options, mat-select options, datepicker cells, etc.)
        if (event.type === 'click') {
          const target = event.target as HTMLElement;
          if (!target.closest('.cdk-overlay-container')) return;
        }
        this.dismissCurrentStrongAlert();
      };
      document.addEventListener('input', this.formChangeHandler);
      document.addEventListener('change', this.formChangeHandler);
      document.addEventListener('click', this.formChangeHandler);
    }, 0);
  }

  private removeFormChangeListener(): void {
    if (this.formChangeHandler !== null) {
      document.removeEventListener('input', this.formChangeHandler);
      document.removeEventListener('change', this.formChangeHandler);
      document.removeEventListener('click', this.formChangeHandler);
      this.formChangeHandler = null;
    }
  }

  private dismissCurrentStrongAlert(): void {
    if (this.strongAlertTimer !== null) {
      clearTimeout(this.strongAlertTimer);
      this.strongAlertTimer = null;
    }
    if (this.currentStrongTimestamp !== null) {
      super.hideAlert(this.currentStrongTimestamp);
      this.currentStrongTimestamp = null;
    }
    this.removeFormChangeListener();
  }
}
